import {
  CONTROL_STATUSES,
  CONTROL_TYPES,
  LEGACY_RISK_MATRIX,
  RA_CATEGORIES,
  RA_DEPARTMENTS,
  type RaMatrixDefinition,
  type RiskControl,
  type RiskHazard,
  riskRating,
  riskSummary,
} from '@felix/domain';
import { ActionIcon, Button, Select, Tabs, Textarea, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Risk.module.css';
import { LevelBadge, RiskStatusBadge } from './RiskParts.js';
import type { RiskAssessmentContent, RiskDetail, RiskMatrixRef } from './types.js';
import { localId, matrixOfSummary, newControl, newHazard } from './view.js';

export type RiskTab = 'general' | 'details' | 'controls' | 'history';

const SCALE = ['1', '2', '3', '4', '5'];

/** Tehlike tablosu (değerlendirme ve şablon): ilk ve kalan risk, 1–5 seçimleri. */
export function HazardEditor({
  hazards,
  readOnly,
  onChange,
  matrix = LEGACY_RISK_MATRIX,
}: {
  hazards: readonly RiskHazard[];
  readOnly: boolean;
  onChange: (hazards: RiskHazard[]) => void;
  /** Seviyelerin matrisi (ADR 0085 §4); verilmezse Legacy 5-band. */
  matrix?: RaMatrixDefinition;
}) {
  const { t } = useTranslation();
  const h = (key: string, options?: Record<string, unknown>) =>
    t(`risk.form.hazards.${key}`, options ?? {});
  const set = (index: number, patch: Partial<RiskHazard>) =>
    onChange(hazards.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const scale = (value: number, label: string, onPick: (v: number) => void) =>
    readOnly ? (
      <span>{value}</span>
    ) : (
      <Select
        aria-label={label}
        size="xs"
        data={SCALE}
        value={String(value)}
        allowDeselect={false}
        onChange={(v) => v && onPick(Number(v))}
        className={classes.narrow}
      />
    );
  return (
    <div>
      {hazards.length === 0 ? <p className={classes.panelNote}>{h('empty')}</p> : null}
      {hazards.length > 0 ? (
        <section
          className={classes.tableWrap}
          aria-label={h('table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${classes.table} ${classes.editTable}`}>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{h('hazard')}</th>
                <th scope="col">{h('consequence')}</th>
                <th scope="col">{h('likelihood')}</th>
                <th scope="col">{h('severity')}</th>
                <th scope="col">{h('initial')}</th>
                <th scope="col">{h('residualLikelihood')}</th>
                <th scope="col">{h('residualSeverity')}</th>
                <th scope="col">{h('residual')}</th>
                {readOnly ? null : <th scope="col" aria-label={h('remove', { n: '' })} />}
              </tr>
            </thead>
            <tbody>
              {hazards.map((item, index) => {
                const initial = riskRating(item.likelihood, item.severity, matrix);
                const residual = riskRating(item.residualLikelihood, item.residualSeverity, matrix);
                const n = index + 1;
                return (
                  <tr key={item.id}>
                    <td>{n}</td>
                    <td className={classes.titleCell}>
                      {readOnly ? (
                        item.hazard
                      ) : (
                        <TextInput
                          size="xs"
                          aria-label={`${h('hazard')} ${n}`}
                          value={item.hazard}
                          maxLength={300}
                          onChange={(e) => set(index, { hazard: e.currentTarget.value })}
                        />
                      )}
                    </td>
                    <td className={classes.titleCell}>
                      {readOnly ? (
                        item.consequence
                      ) : (
                        <TextInput
                          size="xs"
                          aria-label={`${h('consequence')} ${n}`}
                          value={item.consequence}
                          maxLength={500}
                          onChange={(e) => set(index, { consequence: e.currentTarget.value })}
                        />
                      )}
                    </td>
                    <td>
                      {scale(item.likelihood, `${h('likelihood')} ${n}`, (v) =>
                        set(index, { likelihood: v }),
                      )}
                    </td>
                    <td>
                      {scale(item.severity, `${h('severity')} ${n}`, (v) =>
                        set(index, { severity: v }),
                      )}
                    </td>
                    <td>
                      <LevelBadge level={initial.level} score={initial.score} />
                    </td>
                    <td>
                      {scale(item.residualLikelihood, `${h('residualLikelihood')} ${n}`, (v) =>
                        set(index, { residualLikelihood: v }),
                      )}
                    </td>
                    <td>
                      {scale(item.residualSeverity, `${h('residualSeverity')} ${n}`, (v) =>
                        set(index, { residualSeverity: v }),
                      )}
                    </td>
                    <td>
                      <LevelBadge level={residual.level} score={residual.score} />
                    </td>
                    {readOnly ? null : (
                      <td>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={h('remove', { n })}
                          onClick={() => onChange(hazards.filter((_, i) => i !== index))}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ) : null}
      {readOnly ? null : (
        <Button
          mt="sm"
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={() => onChange([...hazards, newHazard(localId())])}
        >
          {h('add')}
        </Button>
      )}
    </div>
  );
}

/** Kontrol önlemi tablosu (düzenleme): tür, önlem, tehlike, sorumlu, son tarih, durum. */
export function ControlEditor({
  controls,
  hazards,
  withDueDate,
  onChange,
}: {
  controls: readonly RiskControl[];
  hazards: readonly RiskHazard[];
  withDueDate: boolean;
  onChange: (controls: RiskControl[]) => void;
}) {
  const { t } = useTranslation();
  const set = (index: number, patch: Partial<RiskControl>) =>
    onChange(controls.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const hazardOptions = [
    { value: '', label: t('risk.form.controls.general') },
    ...hazards.map((h, i) => ({ value: h.id, label: `${i + 1}. ${h.hazard || '…'}` })),
  ];
  return (
    <div>
      {controls.length > 0 ? (
        <section
          className={classes.tableWrap}
          aria-label={t('risk.form.controls.table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${classes.table} ${classes.editTable}`}>
            <thead>
              <tr>
                <th scope="col">{t('risk.controls.columns.type')}</th>
                <th scope="col">{t('risk.controls.columns.measure')}</th>
                <th scope="col">{t('risk.form.controls.hazard')}</th>
                <th scope="col">{t('risk.controls.columns.responsible')}</th>
                {withDueDate ? <th scope="col">{t('risk.controls.columns.dueDate')}</th> : null}
                {withDueDate ? <th scope="col">{t('risk.controls.columns.status')}</th> : null}
                <th scope="col" aria-label={t('risk.form.controls.remove', { n: '' })} />
              </tr>
            </thead>
            <tbody>
              {controls.map((item, index) => {
                const n = index + 1;
                return (
                  <tr key={item.id}>
                    <td>
                      <Select
                        size="xs"
                        aria-label={`${t('risk.controls.columns.type')} ${n}`}
                        allowDeselect={false}
                        value={item.type}
                        data={CONTROL_TYPES.map((v) => ({
                          value: v,
                          label: t(`risk.controlTypes.${v}`),
                        }))}
                        onChange={(v) => v && set(index, { type: v as RiskControl['type'] })}
                      />
                    </td>
                    <td className={classes.titleCell}>
                      <TextInput
                        size="xs"
                        aria-label={`${t('risk.controls.columns.measure')} ${n}`}
                        value={item.measure}
                        maxLength={300}
                        onChange={(e) => set(index, { measure: e.currentTarget.value })}
                      />
                    </td>
                    <td>
                      <Select
                        size="xs"
                        aria-label={`${t('risk.form.controls.hazard')} ${n}`}
                        allowDeselect={false}
                        value={item.hazardId ?? ''}
                        data={hazardOptions}
                        onChange={(v) => set(index, { hazardId: v ? v : null })}
                      />
                    </td>
                    <td>
                      <TextInput
                        size="xs"
                        aria-label={`${t('risk.controls.columns.responsible')} ${n}`}
                        value={item.responsible}
                        maxLength={120}
                        onChange={(e) => set(index, { responsible: e.currentTarget.value })}
                      />
                    </td>
                    {withDueDate ? (
                      <td>
                        <DayInput
                          size="xs"
                          aria-label={`${t('risk.controls.columns.dueDate')} ${n}`}
                          value={item.dueDay}
                          onChange={(v) => set(index, { dueDay: v })}
                        />
                      </td>
                    ) : null}
                    {withDueDate ? (
                      <td>
                        <Select
                          size="xs"
                          aria-label={`${t('risk.controls.columns.status')} ${n}`}
                          allowDeselect={false}
                          value={item.status}
                          data={CONTROL_STATUSES.map((v) => ({
                            value: v,
                            label: t(`risk.controlStatuses.${v}`),
                          }))}
                          onChange={(v) => v && set(index, { status: v as RiskControl['status'] })}
                        />
                      </td>
                    ) : null}
                    <td>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label={t('risk.form.controls.remove', { n })}
                        onClick={() => onChange(controls.filter((_, i) => i !== index))}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ) : (
        <p className={classes.panelNote}>{t('risk.controls.empty')}</p>
      )}
      <Button
        mt="sm"
        size="xs"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={() => onChange([...controls, newControl(localId(), hazards.at(-1)?.id ?? null)])}
      >
        {t('risk.controls.add')}
      </Button>
    </div>
  );
}

export interface RiskFormProps {
  detail: RiskDetail | null;
  draft: RiskAssessmentContent | null;
  editable: boolean;
  dirty: boolean;
  busy: boolean;
  tab: RiskTab;
  notice: { tone: 'info' | 'warning' | 'danger' | 'success'; text: string } | null;
  /** İade bandı (ADR 0116 Ek): bildirimin yerine, iade eden ve gerekçeyle. */
  banner?: ReactNode;
  templateCode: string | null;
  onTab: (tab: RiskTab) => void;
  onChange: (draft: RiskAssessmentContent) => void;
  onSave: () => void;
  onCancel: () => void;
  actions: ReactNode;
  /** Matris sürümünün adı (profil ve sürüm; boş kimlik Legacy 5-band). */
  matrixLabel?: (ref: RiskMatrixRef) => string;
  /** Şirket kütüphanesinden başla (K2; ADR 0086 §7): düzenlenebilir gemi taslağında. */
  library?: ReactNode;
}

/** 3. Risk Assessment Form (New/Edit): General, Risk Details, Control Measures, Revision History. */
export function RiskForm(props: RiskFormProps) {
  const { t } = useTranslation();
  const { detail, draft, editable } = props;
  if (!detail || !draft) {
    return (
      <Panel title={t('risk.form.title')}>
        <EmptyState title={t('risk.form.noneTitle')} description={t('risk.form.noneText')} />
      </Panel>
    );
  }
  const f = (key: string) => t(`risk.form.${key}`);
  const change = (patch: Partial<RiskAssessmentContent>) => props.onChange({ ...draft, ...patch });
  // Değerlendirmenin matrisi (ADR 0085 §4): gönderilmişte kendi sürümü, taslakta geçerli sürüm
  const matrix = matrixOfSummary(detail);
  const summary = riskSummary(draft.hazards, matrix);
  const readOnly = !editable;
  const matrixLabel = props.matrixLabel;
  return (
    <Panel title={t('risk.form.title')}>
      <div className={classes.meta}>
        <strong>{detail.raNo}</strong>
        <RiskStatusBadge item={detail} />
        {detail.revisionNo ? (
          <span>{t('risk.form.revision', { no: detail.revisionNo })}</span>
        ) : null}
        {detail.inForceRevision && detail.inForceRevision !== detail.revisionNo ? (
          <span>{t('risk.form.inForce', { no: detail.inForceRevision })}</span>
        ) : null}
        {props.templateCode ? (
          <span>{t('risk.form.source', { code: props.templateCode })}</span>
        ) : null}
        {matrixLabel && detail.matrix ? (
          <span>{t('risk.form.matrix', { name: matrixLabel(detail.matrix) })}</span>
        ) : null}
      </div>
      <Tabs value={props.tab} onChange={(v) => v && props.onTab(v as RiskTab)} mt="xs">
        <Tabs.List>
          <Tabs.Tab value="general">{f('tabs.general')}</Tabs.Tab>
          <Tabs.Tab value="details">{f('tabs.details')}</Tabs.Tab>
          <Tabs.Tab value="controls">{f('tabs.controls')}</Tabs.Tab>
          <Tabs.Tab value="history">{f('tabs.history')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="general">
          <div className={classes.formGrid}>
            <TextInput label={f('vessel')} value={detail.vesselName} readOnly />
            <Select
              label={f('department')}
              required
              readOnly={readOnly}
              allowDeselect={false}
              value={draft.department}
              data={RA_DEPARTMENTS.map((v) => ({ value: v, label: t(`risk.departments.${v}`) }))}
              onChange={(v) =>
                v && change({ department: v as RiskAssessmentContent['department'] })
              }
            />
            <Select
              label={f('category')}
              required
              readOnly={readOnly}
              allowDeselect={false}
              value={draft.category}
              data={RA_CATEGORIES.map((v) => ({ value: v, label: t(`risk.categories.${v}`) }))}
              onChange={(v) => v && change({ category: v as RiskAssessmentContent['category'] })}
            />
            <TextInput
              className={classes.span2}
              label={f('titleField')}
              required
              readOnly={readOnly}
              value={draft.title}
              maxLength={200}
              onChange={(e) => change({ title: e.currentTarget.value })}
            />
            <TextInput
              label={f('location')}
              readOnly={readOnly}
              value={draft.location ?? ''}
              maxLength={200}
              onChange={(e) => change({ location: e.currentTarget.value || null })}
            />
            <Textarea
              className={classes.span3}
              label={f('activity')}
              required
              readOnly={readOnly}
              value={draft.activity}
              maxLength={2000}
              autosize
              minRows={2}
              maxRows={6}
              description={`${draft.activity.length}/2000`}
              onChange={(e) => change({ activity: e.currentTarget.value })}
            />
            <div>
              <span className={classes.fieldLabel}>{f('likelihood')}</span>
              <span className={classes.readValue}>
                {summary.initial
                  ? `${summary.initial.likelihood} – ${t(`risk.scale.${summary.initial.likelihood}`)}`
                  : '—'}
              </span>
            </div>
            <div>
              <span className={classes.fieldLabel}>{f('impact')}</span>
              <span className={classes.readValue}>
                {summary.initial
                  ? `${summary.initial.severity} – ${t(`risk.scale.${summary.initial.severity}`)}`
                  : '—'}
              </span>
            </div>
            <div>
              <span className={classes.fieldLabel}>{f('level')}</span>
              <span className={classes.readValue}>
                {summary.initial ? (
                  <LevelBadge level={summary.initial.level} score={summary.initial.score} />
                ) : (
                  t('risk.list.unscored')
                )}
                {summary.residual ? (
                  <StatusBadge tone="neutral" withIcon={false} variant="dot">
                    {f('residual')}: {t(`risk.levels.${summary.residual.level}`)} (
                    {summary.residual.score})
                  </StatusBadge>
                ) : null}
              </span>
            </div>
            <TextInput label={f('assessedBy')} value={detail.createdByName ?? '—'} readOnly />
            <DayInput
              label={f('assessmentDate')}
              required
              readOnly={readOnly}
              value={draft.assessmentDay}
              onChange={(v) => v && change({ assessmentDay: v })}
            />
            <DayInput
              label={f('reviewDate')}
              required
              readOnly={readOnly}
              value={draft.reviewDay}
              minDate={draft.assessmentDay}
              onChange={(v) => v && change({ reviewDay: v })}
            />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="details" pt="sm">
          {props.library ?? null}
          <HazardEditor
            hazards={draft.hazards}
            readOnly={readOnly}
            matrix={matrix}
            onChange={(hazards) =>
              change({
                hazards,
                // Kaldırılan tehlikenin önlemi genel önleme döner
                controls: draft.controls.map((c) =>
                  c.hazardId && !hazards.some((h) => h.id === c.hazardId)
                    ? { ...c, hazardId: null }
                    : c,
                ),
              })
            }
          />
        </Tabs.Panel>
        <Tabs.Panel value="controls" pt="sm">
          {readOnly ? (
            <ControlTable controls={draft.controls} />
          ) : (
            <ControlEditor
              controls={draft.controls}
              hazards={draft.hazards}
              withDueDate
              onChange={(controls) => change({ controls })}
            />
          )}
        </Tabs.Panel>
        <Tabs.Panel value="history" pt="sm">
          <RevisionHistory detail={detail} {...(matrixLabel ? { matrixLabel } : {})} />
        </Tabs.Panel>
      </Tabs>
      {props.banner ? (
        props.banner
      ) : props.notice ? (
        <p className={classes.notice} data-tone={props.notice.tone} role="status">
          {props.notice.text}
        </p>
      ) : null}
      <div className={classes.formActions}>
        {editable ? (
          <>
            <Button size="sm" onClick={props.onSave} disabled={!props.dirty} loading={props.busy}>
              {f('save')}
            </Button>
            <Button size="sm" variant="default" onClick={props.onCancel} disabled={!props.dirty}>
              {f('cancel')}
            </Button>
          </>
        ) : null}
        {props.actions}
      </div>
    </Panel>
  );
}

/** Önlem tablosu (okuma): tür, önlem, sorumlu, son tarih, durum. */
export function ControlTable({ controls }: { controls: readonly RiskControl[] }) {
  const { t } = useTranslation();
  if (controls.length === 0) return <p className={classes.panelNote}>{t('risk.controls.empty')}</p>;
  const tone = (status: RiskControl['status']) =>
    status === 'done'
      ? 'success'
      : status === 'in_progress'
        ? 'warning'
        : status === 'open'
          ? 'info'
          : 'neutral';
  return (
    <section
      className={classes.tableWrap}
      aria-label={t('risk.controls.table')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={classes.table}>
        <thead>
          <tr>
            <th scope="col">{t('risk.controls.columns.type')}</th>
            <th scope="col">{t('risk.controls.columns.measure')}</th>
            <th scope="col">{t('risk.controls.columns.responsible')}</th>
            <th scope="col">{t('risk.controls.columns.dueDate')}</th>
            <th scope="col">{t('risk.controls.columns.status')}</th>
          </tr>
        </thead>
        <tbody>
          {controls.map((c) => (
            <tr key={c.id}>
              <td>{t(`risk.controlTypes.${c.type}`)}</td>
              <td>{c.measure}</td>
              <td>{c.responsible}</td>
              <td className={classes.nowrap}>{formatDay(c.dueDay)}</td>
              <td>
                <StatusBadge tone={tone(c.status)} withIcon={false}>
                  {t(`risk.controlStatuses.${c.status}`)}
                </StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RevisionHistory({
  detail,
  matrixLabel,
}: {
  detail: RiskDetail;
  matrixLabel?: (ref: RiskMatrixRef) => string;
}) {
  const { t } = useTranslation();
  const h = (key: string, options?: Record<string, unknown>) =>
    t(`risk.form.history.${key}`, options ?? {});
  if (detail.revisions.length === 0) return <p className={classes.panelNote}>{h('empty')}</p>;
  const tone = (outcome: string | null) =>
    outcome === 'approved'
      ? 'success'
      : outcome === 'returned'
        ? 'warning'
        : outcome === 'pending'
          ? 'info'
          : 'neutral';
  return (
    <ol className={classes.history}>
      {[...detail.revisions].reverse().map((r) => (
        <li key={r.revisionNo}>
          <div className={classes.historyHead}>
            <span>{t('risk.form.revision', { no: r.revisionNo })}</span>
            {r.outcome ? (
              <StatusBadge tone={tone(r.outcome)} withIcon={false}>
                {h(`outcomes.${r.outcome}`)}
              </StatusBadge>
            ) : null}
          </div>
          <div className={classes.sub}>
            {h('submitted', {
              at: formatDay(r.submittedAt.slice(0, 10)),
              by: r.submittedByName ?? '—',
            })}
          </div>
          {r.reason ? <div>{h('reason', { reason: r.reason })}</div> : null}
          {matrixLabel && r.matrix !== undefined ? (
            <div className={classes.sub}>
              {h('matrix', {
                name: matrixLabel(r.matrix ?? { versionId: null, version: null }),
              })}
            </div>
          ) : null}
          {r.decisions.map((d) => (
            <div key={`${d.step}-${d.at}`} className={classes.sub}>
              {h('decision', {
                step: d.step,
                decision: t(`risk.form.history.outcomes.${d.decision}`).toLowerCase(),
                by: d.byName ?? '—',
                role: d.office ? `${d.roleName ?? ''}, ${h('office')}` : (d.roleName ?? ''),
                at: formatDay(d.at.slice(0, 10)),
              })}
              {d.reason ? ` — ${d.reason}` : ''}
            </div>
          ))}
        </li>
      ))}
    </ol>
  );
}
